import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  ListGroup
} from 'react-bootstrap';
import {
  FaUsers,
  FaUserPlus,
  FaUserMinus,
  FaCheck,
  FaTimes
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import './css/RoleAssignmentModal.css';

const RoleAssignmentModal = ({ show, onHide, layout, roles, onRolesAssigned }) => {
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [conflictingRoles, setConflictingRoles] = useState([]);
  const [availableRolesWithPermissions, setAvailableRolesWithPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (show && layout && roles) {
      loadLayoutRoles();
    }
  }, [show, layout, roles]);

  const loadLayoutRoles = async () => {
    try {
      setLoading(true);

      // Fetch current role assignments for this layout
      const api = new DataApi(`objectconfig/layouts/${layout.layout_id}/roles`);
      const response = await api.fetchAll();

      if (response?.data?.success) {
        const {
          assignedRoles: currentRoles,
          conflictingRoles,
          availableRolesWithPermissions
        } = response.data.data || {
          assignedRoles: [],
          conflictingRoles: [],
          availableRolesWithPermissions: []
        };

        // Transform assigned roles to match the structure of available roles
        const transformedAssignedRoles = currentRoles.map(assignedRole => ({
          id: assignedRole.role_id,
          name: assignedRole.role_name,
          description: assignedRole.role_description,
          is_active: assignedRole.role_is_active
        }));

        setAssignedRoles(transformedAssignedRoles);

        // Use the permission-filtered roles from backend instead of all roles
        const permissionFilteredRoles = availableRolesWithPermissions.map(role => ({
          id: role.id,
          name: role.name,
          description: role.description || '',
          is_active: role.is_active,
          can_read: role.can_read,
          can_update: role.can_update,
          can_create: role.can_create,
          can_delete: role.can_delete
        }));

        // Filter out already assigned roles and conflicting roles from permission-filtered roles
        const available = permissionFilteredRoles.filter(role =>
          !transformedAssignedRoles.some(assigned => assigned.id === role.id) &&
          !conflictingRoles.some(conflicting => conflicting.role_id === role.id)
        );

        setAvailableRoles(available);
        setConflictingRoles(conflictingRoles);
        setAvailableRolesWithPermissions(availableRolesWithPermissions);

        console.log('Permission filtered roles:', permissionFilteredRoles);
        console.log('Available roles after filtering:', available);
      } else {
        setAssignedRoles([]);
        setAvailableRoles([]);
        setConflictingRoles([]);
      }
    } catch (error) {
      console.error('Error loading layout roles:', error);
      setAssignedRoles([]);
      setAvailableRoles(roles);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = (role) => {
    setAssignedRoles([...assignedRoles, role]);
    setAvailableRoles(availableRoles.filter(r => r.id !== role.id));
  };

  const handleRemoveRole = (role) => {
    setAssignedRoles(assignedRoles.filter(r => r.id !== role.id));
    setAvailableRoles([...availableRoles, role]);
  };

  const handleSaveAssignments = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const roleIds = assignedRoles.map(role => role.id);

      const api = new DataApi(`objectconfig/layouts/${layout.layout_id}/roles`);
      const response = await api.create({ roleAssignments: roleIds });

      if (response?.data?.success) {
        setSuccess('Roles assigned successfully!');
        if (onRolesAssigned) {
          onRolesAssigned({
            ...layout,
            roles: assignedRoles
          });
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to assign roles');
      }
    } catch (error) {
      console.error('Error assigning roles:', error);
      setError('Failed to assign roles: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getRolePermissions = (role) => {
    const permissions = [];
    if (role.can_read) permissions.push('Read');
    if (role.can_create) permissions.push('Create');
    if (role.can_update) permissions.push('Update');
    if (role.can_delete) permissions.push('Delete');
    return permissions;
  };

  const getPermissionBadge = (permission) => {
    const variants = {
      'Read': 'info',
      'Create': 'success',
      'Update': 'warning',
      'Delete': 'danger'
    };
    return <Badge bg={variants[permission] || 'secondary'} size="sm">{permission}</Badge>;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="role-assignment-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUsers className="me-2" />
          Assign Roles to Layout
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="role-assignment-content">
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <div className="layout-info mb-3">
          <h6>Layout: {layout?.layout_label || layout?.layout_name}</h6>
          <p className="text-muted mb-0">
            Select roles that should have access to this layout
          </p>
          <div className="permission-requirements mt-2">
            <small className="text-info">
              <strong>Required Permissions:</strong>
              {layout?.layout_type?.toLowerCase() === 'detail' && ' Read permission'}
              {layout?.layout_type?.toLowerCase() === 'edit' && ' Update permission'}
              {layout?.layout_type?.toLowerCase() === 'list' && ' Read permission'}
            </small>
          </div>
        </div>

        <Row>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">
                  <FaUserPlus className="me-2" />
                  Available Roles ({availableRoles.length})
                </h6>
              </Card.Header>
              <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loading ? (
                  <div className="text-center py-3">
                    <div className="loading-spinner mb-2"></div>
                    <p>Loading roles...</p>
                  </div>
                ) : availableRoles.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    <FaUsers size={24} className="mb-2" />
                    <p>All roles are already assigned</p>
                  </div>
                ) : (
                  <ListGroup variant="flush">
                    {availableRoles.map(role => (
                      <ListGroup.Item
                        key={role.id}
                        className="role-item"
                        action
                        onClick={() => handleAssignRole(role)}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{role.name}</strong>
                            <br />
                            <small className="text-muted">{role.description || 'No description'}</small>
                          </div>
                          <Button variant="outline-primary" size="sm">
                            <FaUserPlus />
                          </Button>
                        </div>
                        <div className="role-permissions mt-2">
                          {getRolePermissions(role).map(permission => (
                            <span key={permission} className="me-1">
                              {getPermissionBadge(permission)}
                            </span>
                          ))}
                        </div>
                        <div className="role-permission-details mt-1">
                          <small className="text-success">
                            ✓ Has required permissions for this layout type
                          </small>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">
                  <FaCheck className="me-2 text-white" />
                  Assigned Roles ({assignedRoles.length})
                </h6>
              </Card.Header>
              <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {assignedRoles.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    <FaUsers size={24} className="mb-2" />
                    <p>No roles assigned yet</p>
                    <small>Click on roles from the left panel to assign them</small>
                  </div>
                ) : (
                  <ListGroup variant="flush">
                    {assignedRoles.map(role => (
                      <ListGroup.Item
                        key={role.id}
                        className="role-item assigned"
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong>{role.name}</strong>
                            <br />
                            <small className="text-muted">{role.description || 'No description'}</small>
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveRole(role)}
                          >
                            <FaUserMinus />
                          </Button>
                        </div>
                        <div className="role-permissions mt-2">
                          {getRolePermissions(role).map(permission => (
                            <span key={permission} className="me-1">
                              {getPermissionBadge(permission)}
                            </span>
                          ))}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {conflictingRoles.length > 0 && (
          <Row className="mt-3">
            <Col>
              <Card className="border-warning">
                <Card.Header className="bg-warning text-dark">
                  <h6 className="mb-0">
                    <FaTimes className="me-2" />
                    Conflicting Role Assignments ({conflictingRoles.length})
                  </h6>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3">
                    These roles are already assigned to other layouts of the same type and cannot be assigned to this layout:
                  </p>
                  <ListGroup variant="flush">
                    {conflictingRoles.map(conflict => (
                      <ListGroup.Item key={conflict.role_id} className="text-muted">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{conflict.role_name}</strong>
                            <br />
                            <small>Already assigned to: {conflict.layout_name} ({conflict.layout_type})</small>
                          </div>
                          <Badge bg="warning" text="dark">Conflicting</Badge>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <div className="assignment-summary mt-3">
          <Card>
            <Card.Body>
              <h6>Assignment Summary</h6>
              <Row>
                <Col md={3}>
                  <div className="summary-item">
                    <span className="summary-label">Total Roles:</span>
                    <span className="summary-value">{roles.length}</span>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="summary-item">
                    <span className="summary-label">With Permissions:</span>
                    <span className="summary-value text-primary">{availableRolesWithPermissions?.length || 0}</span>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="summary-item">
                    <span className="summary-label">Available:</span>
                    <span className="summary-value text-info">{availableRoles.length}</span>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="summary-item">
                    <span className="summary-label">Assigned:</span>
                    <span className="summary-value text-success">{assignedRoles.length}</span>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveAssignments}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Assignments'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RoleAssignmentModal;
